import { clearAcquisition, storedAcquisition } from '../analytics/acquisition';
import { clearPartnerLink } from '../partners/partner-code';
import { visitorId } from '../analytics/visitor';
import type { DataSource } from '../api/data-source';
import { ApiError } from '../api/http';
import type { MyInvitation, Principal } from '../api/types';
import { signOutIdp } from './firebase';

export type AuthStatus = 'unknown' | 'signed-out' | 'signed-in';

export interface AuthState {
  status: AuthStatus;
  principal: Principal | null;
  /** The last session was refused for lacking a second step; sign-in says so and asks for the code. */
  secondFactorRequired?: boolean;
}

type Listener = () => void;

/**
 * The session as the app sees it: `GET /me` decides. Framework-free so the router's `beforeLoad`
 * can wait on it; React reads it through `useAuth` (useSyncExternalStore).
 */
export class AuthStore {
  private state: AuthState = { status: 'unknown', principal: null };
  private readonly listeners = new Set<Listener>();
  private pending: Promise<Principal | null> | null = null;
  private leaving: Principal | null = null;

  constructor(private readonly ds: DataSource) {}

  getState = (): AuthState => this.state;

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  /**
   * The person who just signed out or whose session just ended, until the next screen has arrived.
   * The session ends a moment before the router has the sign-in page, and the screen still mounted
   * reads its person meanwhile: with none it threw, and the failure screen flashed before sign-in
   * (review 2026-10-06). The route gates never read this; only `usePrincipal` does.
   */
  departing = (): Principal | null => this.leaving;

  /** The router has shown the next screen: nobody is on their way out any more. */
  arrived(): void {
    this.leaving = null;
  }

  /** The principal, loading it once if unknown; null when signed out. */
  ensure(): Promise<Principal | null> {
    if (this.state.status !== 'unknown') return Promise.resolve(this.state.principal);
    this.pending ??= this.load().finally(() => (this.pending = null));
    return this.pending;
  }

  /**
   * The principal for a page that does not need one. An API that cannot answer answers `null`
   * here rather than throwing: the sign-in and sign-up screens are the pages a person reaches for
   * when Paige is having trouble, and they must render whatever `/me` says or fails to say. The
   * status stays `unknown`, so the next call asks again.
   */
  async ensureOrNone(): Promise<Principal | null> {
    try {
      return await this.ensure();
    } catch {
      return null;
    }
  }

  /**
   * `emailCode`: the code Paige mailed to the address, when the first try was refused `email_not_verified`.
   * `invitation`: the company whose invitation's link the sign-up came from; a new account made from it joins.
   */
  async signIn(idToken: string, emailCode?: string, invitation?: string): Promise<Principal> {
    const principal = await this.ds.session.create(idToken, emailCode, invitation);
    this.set({ status: 'signed-in', principal });
    return principal;
  }

  /** The invitations waiting for the person signed in: they join a company only by accepting. */
  invitations(): Promise<MyInvitation[]> {
    return this.ds.session.invitations();
  }

  /**
   * Whether any invitation waits, asked after a sign-in to decide whether to show them. A read that
   * fails is "none": the person goes on to where they were going, and finds them under their mark.
   */
  async invitationsWaiting(): Promise<boolean> {
    try {
      return (await this.invitations()).length > 0;
    } catch {
      return false;
    }
  }

  /** Accepting makes the person a member: the principal it answers is the one the app goes on with. */
  async acceptInvitation(id: string): Promise<Principal> {
    const principal = await this.ds.session.acceptInvitation(id);
    this.set({ status: 'signed-in', principal });
    return principal;
  }

  declineInvitation(id: string): Promise<void> {
    return this.ds.session.declineInvitation(id);
  }

  /** Confirms the token's address with the mailed code, without signing in: two-step sign-in needs a confirmed address. */
  confirmEmail(idToken: string, emailCode: string): Promise<void> {
    return this.ds.session.confirmEmail(idToken, emailCode);
  }

  /** Asks Paige to mail the code that proves the token's address is the person's. */
  sendEmailCode(idToken: string): Promise<void> {
    return this.ds.session.sendEmailCode(idToken);
  }

  /** Asks Paige to mail the link for a forgotten password; it answers the same for every address. */
  requestPasswordReset(email: string): Promise<void> {
    return this.ds.session.requestPasswordReset(email);
  }

  /** Sets the new password with the mailed link's token; the person signs in with it afterwards. */
  resetPassword(token: string, password: string): Promise<void> {
    return this.ds.session.resetPassword(token, password);
  }

  /** A partner code, when given, was checked by the API before the company was made (docs/partners.md). */
  async signUp(idToken: string, company: string, partnerCode?: string): Promise<Principal> {
    const principal = await this.ds.session.signUp(
      idToken,
      company,
      { ...storedAcquisition(), visitor: visitorId() },
      partnerCode,
    );
    clearAcquisition();
    // Kept when sign-up went without it (partner codes could not be checked), for the Billing card.
    if (partnerCode) clearPartnerLink();
    this.set({ status: 'signed-in', principal });
    return principal;
  }

  async signOut(): Promise<void> {
    await Promise.allSettled([this.ds.session.destroy(), signOutIdp()]);
    this.set({ status: 'signed-out', principal: null });
  }

  /** Ends the session here without asking: for a caller that already knows it ended elsewhere. */
  expire(): void {
    if (this.state.status === 'signed-in') this.set({ status: 'signed-out', principal: null });
  }

  /**
   * Asks `GET /me` again after a 401, which may be one action's refusal (allowing a device) or a
   * company the person is no longer in rather than the session's end: signed out only when `/me`
   * says so too, never on the request's word alone. The principal it leaves is the fresh one.
   */
  async recheck(): Promise<void> {
    try {
      await this.load();
    } catch {
      // The API could not answer: the session stands as it was.
    }
  }

  /**
   * Called when a company refuses the session for lacking a second step (403 `mfa_required`): the
   * session is over; sign-in enrols the person if need be and asks for the code.
   */
  requireSecondFactor(): void {
    this.set({ status: 'signed-out', principal: null, secondFactorRequired: true });
    // The server session is ended too: left alive, the sign-in page would find it and send the
    // person straight back to where they were refused, with no word (Dustin on prod, 2026-09-21).
    void Promise.allSettled([this.ds.session.destroy(), signOutIdp()]);
  }

  private async load(): Promise<Principal | null> {
    try {
      const principal = await this.ds.session.getMe();
      this.set({ status: 'signed-in', principal });
      return principal;
    } catch (e) {
      if (e instanceof ApiError && e.unauthenticated) {
        this.set({ status: 'signed-out', principal: null });
        return null;
      }
      throw e;
    }
  }

  private set(state: AuthState): void {
    this.leaving = state.principal ? null : (this.state.principal ?? this.leaving);
    this.state = state;
    for (const l of this.listeners) l();
  }
}
